import { useRef } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import Zoom from 'react-medium-image-zoom';

import {
  faCompress,
  faExpand,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import optainScanningImg from 'src/assets/img/optain-scanning.png';

import { QualityPrecheckEnum } from 'src/models/Grading.model';

import ImagingInProgress from '../ImagingInProgress';
import ImageStatusText from './ImageStatusText';

interface IImageUploadColumnProps {
  imageUrl: string;
  eyeLabel: string;
  uploadLabel: string;
  quality?: string;
  id?: string;
  hasQualityPrecheck?: boolean;
  isUploading?: boolean;
  onImageSelect: (imageUrl: string) => void;
}

const ImageUploadColumn = ({
  imageUrl,
  eyeLabel,
  uploadLabel,
  quality,
  id,
  hasQualityPrecheck = true,
  isUploading = false,
  onImageSelect,
}: IImageUploadColumnProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const UnzoomIcon = () => <FontAwesomeIcon icon={faCompress} />;

  const handleUpload = () => {
    const fileInput = fileInputRef.current;
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (fileInput?.files && fileInput.files[0]) {
      const file = fileInput.files[0];
      if (file.type === 'image/jpeg') {
        const url = URL.createObjectURL(file);
        onImageSelect(url);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.id === id) {
      event.preventDefault(); // allow dropping
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();

    if (event.dataTransfer) {
      const target = event.target as HTMLElement;
      if (target.id === id) {
        const imageUrl = event.dataTransfer.getData('text/plain');
        onImageSelect(imageUrl);
      }
    }
  };

  return (
    <div style={{ flex: 1 }}>
      <div className="d-flex flex-column w-100">
        <div>
          <div
            className="overflow-hidden mw-100 d-flex align-items-center justify-content-center"
            style={{ maxHeight: '60vh' }}
          >
            {imageUrl ? (
              <div className="position-relative d-flex align-items-center justify-content-center">
                <Zoom classDialog="custom-zoom" IconUnzoom={UnzoomIcon}>
                  <img
                    src={imageUrl || optainScanningImg}
                    id={id}
                    alt={eyeLabel}
                    className="img-fluid mw-100 w-auto h-auto"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    draggable={false}
                    style={{
                      maxHeight: window.innerWidth > 2000 ? '60vh' : '43vh',
                      objectFit: 'contain',
                    }}
                  />
                </Zoom>

                {/* Native zoom icon prop doesn't work, have to do this instead */}
                <div
                  className="position-absolute bg-white rounded-2 p-2 pe-none d-flex align-items-center justify-content-center"
                  style={{
                    bottom: '8px',
                    right: '8px',
                    fontSize: '14px',
                    zIndex: 1,
                    minWidth: '32px',
                    minHeight: '32px',
                  }}
                >
                  <FontAwesomeIcon icon={faExpand} style={{ color: '#666' }} />
                </div>
              </div>
            ) : (
              <ImagingInProgress
                id={id}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              />
            )}
          </div>
          <Badge
            bg="dark"
            className="position-relative start-50 translate-middle-x"
            style={{ top: -25 }}
          >
            {eyeLabel}
          </Badge>
        </div>

        <div className="col-11 mx-auto">
          <Card
            bg={
              quality === QualityPrecheckEnum.Usable
                ? 'success'
                : quality === QualityPrecheckEnum.Reject
                  ? 'danger'
                  : 'gray-100'
            }
          >
            <Card.Body>
              <Card.Text
                className={
                  quality === QualityPrecheckEnum.Usable ||
                  quality === QualityPrecheckEnum.Reject
                    ? 'text-white'
                    : ''
                }
              >
                <ImageStatusText
                  imageUrl={imageUrl}
                  quality={quality}
                  hasQualityPrecheck={hasQualityPrecheck}
                  isUploading={isUploading}
                  isUploadMode={true}
                />
              </Card.Text>
            </Card.Body>
          </Card>

          <Button
            variant="primary"
            onClick={handleUpload}
            className="w-100 my-2"
          >
            <FontAwesomeIcon icon={faUpload} className="me-2" />
            <span>{uploadLabel}</span>
          </Button>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        className="d-none"
        accept=".jpg, .jpeg"
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default ImageUploadColumn;
